import { CSSProperties, ReactNode, useState } from 'react';
import { PageSubHeaderView } from './page-sub-header.view';
import { PageHeaderView } from './page-header.view';
import { VerticalNavView } from '../vertical-nav/vertical-nav.view';
import { IPageLink } from '@vivli/shared/infrastructure/interface';
import { LoadIndicatorCenteredView } from '../../load-indicator/load-indicator-centered.view';

const containerStyle: CSSProperties = {
    backgroundColor: 'rgb(233, 234, 239)',
    overflowY: 'auto',
    overflowX: 'hidden',
    transform: 'translate3d(0,0,0)', // force chrome to use GPU for rendering - fixes UI 'whiteout' glitch related to scrollbars (I think),
    display: 'grid',
    flexDirection: 'column',
    gridTemplateRows: 'auto 1fr',
    width: '100%',
    height: '100%',
    position: 'relative'
};

const loadingStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    zIndex: 99999
};

const pageStyle: CSSProperties = {
    display: 'grid',
    flexDirection: 'column',
    gridTemplateColumns: 'auto 1fr',
    height: '100%',
    overflow: 'hidden'
};

const bodyStyle: CSSProperties = {
    display: 'grid',
    flexDirection: 'row',
    gridTemplateRows: 'auto 1fr',
    rowGap: '20px',
    overflowY: 'auto'
};

const componentStyle: CSSProperties = {
    padding: '40px'
};

interface PageViewProps {
    title?: string;
    subTitle?: string;
    customHeader?: ReactNode;
    exportToCsv?: () => void;
    headerContent?: ReactNode;
    backButtonText?: string;
    onBackClick?: () => void;
    isLoading?: boolean;
    defaultLink?: IPageLink;
    links: IPageLink[];
    onTabClick?: (link: IPageLink) => void;
    // Optional interceptor run before a tab switch; return false (or a Promise resolving false) to
    // veto the switch (nothing changes). Used to confirm before leaving during a file upload.
    beforeTabChange?: (link: IPageLink) => boolean | Promise<boolean>;
}

export const PageView = ({
    title,
    subTitle,
    customHeader,
    exportToCsv,
    onTabClick,
    links,
    defaultLink,
    headerContent,
    backButtonText,
    onBackClick,
    isLoading,
    beforeTabChange
}: PageViewProps) => {
    const [activeLink, setActiveLink] = useState<IPageLink>(defaultLink);

    const handleNavTabClick = async (link: IPageLink) => {
        if (isLoading) {
            return;
        }

        // Allow a consumer to veto the switch (e.g. confirm before leaving during an upload). Gating
        // here, at the click origin, means a veto changes neither the highlight nor the body. Skipped
        // when the user re-clicks the tab they are already on, so no pointless prompt appears.
        if (link?.url !== activeLink?.url && beforeTabChange && !(await beforeTabChange(link))) {
            return;
        }

        setActiveLink(link);
        onTabClick && onTabClick(link);
    };

    return (
        <div style={containerStyle}>
            <PageHeaderView backButtonText={backButtonText} title={title} subTitle={subTitle} onBackClick={onBackClick}>
                {headerContent}
            </PageHeaderView>
            <div style={pageStyle}>
                <VerticalNavView onTabClick={handleNavTabClick} links={links} activeLink={activeLink} isLoading={isLoading} />
                <div style={bodyStyle} className={'scrolly'}>
                    {activeLink?.pageTitle && (
                        <PageSubHeaderView title={activeLink?.pageTitle} customHeader={customHeader} exportToCsv={exportToCsv} />
                    )}
                    <div style={componentStyle}>
                        {isLoading && (
                            <div style={loadingStyle}>
                                <LoadIndicatorCenteredView />
                            </div>
                        )}
                        {activeLink?.component}
                    </div>
                </div>
            </div>
        </div>
    );
};
